import React, { PropTypes, Component } from 'react'
import Grid, { Half } from 'grid-styled'
import Promise from 'bluebird'
import { fromJS } from 'immutable'
import styled from 'styled-components'
import { translate } from 'react-i18next'
import R from 'ramda'
import CardWithImage from '../../components/CardWithImage'
import ThemeContainer from '../../components/ThemeContainer'
import { Metrics, Fonts, Colors } from '../../Themes'
import Key from '../../svg-icons/Key'
import { searchCategories, searchCities } from '../../sources/SearchSource'
import Dropdown from '../../components/Dropdown'
import defaultCityImage from '../../../public/img/Defaults/default_city.jpg'
import { sortOptionsDefault } from '../../Utils/Defaults'
import { SortTypes, numberWithCommas } from '../../Utils'

const CardContainer = styled(Half)`
  margin-top: ${Metrics.margins.larger};
  padding-left: ${Metrics.margins.small};
  padding-right: ${Metrics.margins.small};
  transition: ease 0.5s transform;
  cursor: pointer;

  &:hover {
    transform: scale(1.02);
    transition: ease 0.5s transform;
  }
`

const NoProperty = styled.div`
  text-align: center;
  ${Fonts.style.Text};
  margin-top: ${Metrics.margins.larger};
`

const Title = styled.div`
  ${Fonts.style.Input};
  color: ${Colors.black};
`

const DropdownTitle = styled.div`${Fonts.style.Label};`

const Category = styled.div`
  ${Fonts.style.Input};
  color: ${Colors.primary};
  margin-top: ${Metrics.margins.tiny};
`

const Description = styled.div`
  padding-top: ${Metrics.margins.small};
  ${Fonts.style.Small};
  color: ${Colors.black};
  display: -webkit-box;
  height: ${Metrics.margins.big};
  margin: 0 auto;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

const Info = styled.div`
  margin-bottom: ${Metrics.margins.small};
  ${Fonts.style.Input};
  color: ${Colors.black};
  span {
    color: ${Colors.primary};
  }
`

const SortContainer = styled(Grid)`
  padding-left: ${Metrics.margins.small};
  padding-right: ${Metrics.margins.small};

  .sort-title {
    color: black;
    margin: ${Metrics.margins.small} 0;
  }
`

const Container = styled.div`margin-top: ${Metrics.margins.larger};`

const CustomThemeContainer = styled(ThemeContainer)`
  height: calc(100vh - ${Metrics.components.navbarDefault});
  overflow-y: scroll;
`

class FavoriteProperties extends Component {
  constructor (props) {
    super(props)
    this.state = {
      properties: props.user.preferredProperties || [],
      sortOptions: R.clone(sortOptionsDefault),
      selectedSort: null
    }
    this.handleSort = this.handleSort.bind(this)
  }

  shouldComponentUpdate (nextProps, nextState) {
    const { properties, selectedSort, sortOptions } = this.state
    const { user } = this.props
    if (
      !fromJS(user).equals(fromJS(nextProps.user)) ||
      !fromJS(properties).equals(fromJS(nextState.properties)) ||
      !fromJS(sortOptions).equals(fromJS(nextState.sortOptions)) ||
      selectedSort !== nextState.selectedSort
    ) {
      return true
    }
    return false
  }

  componentWillMount () {
    const { user } = this.props

    let properties = R.clone(user.preferredProperties)
    let ids = []

    if (properties.length > 0) {
      properties.forEach(el => {
        ids = R.union(ids, [ el.city ])
      })

      Promise.all([ searchCategories(), searchCities({ where: { id: ids } }) ])
        .spread((categoriesResponse, citiesReponse) => {
          const categoriesHash = {}
          let citiesHash = {}
          categoriesResponse.data.forEach(category => {
            categoriesHash[category.id] = category
          })

          citiesReponse.data.forEach(city => {
            citiesHash[city.id] = city.picture || defaultCityImage
          })

          properties = R.map(d => {
            d.category = categoriesHash[d.category].name
            d.picture = d.useDefaultPictures ? citiesHash[d.city] : d.picture
            return d
          }, properties)

          this.setState({ properties })
        })
        .catch(console.error)
    }
  }

  handleSort (el) {
    const { properties } = this.state

    if (!el || properties.length === 0) {
      return
    }

    const key = el.key
    const direction = el.direction
    properties.sort((elementA, elementB) => {
      const valueA = elementA[key]
      const valueB = elementB[key]
      let sortVal = 0

      if (valueA > valueB) {
        sortVal = 1
      }
      if (valueA < valueB) {
        sortVal = -1
      }
      if (sortVal !== 0 && direction === SortTypes.DESC) {
        sortVal *= -1
      }
      return sortVal
    })

    this.setState({
      properties,
      selectedSort: el
    })
  }

  render () {
    const { properties, selectedSort, sortOptions } = this.state
    const { t, router } = this.props
    const list =
      properties.length > 0 &&
      properties.map((el, index) => {
        return (
          <CardContainer key={`property-${index}`} xs={1} sm={1}>
            <CardWithImage
              imgSrc={el.picture}
              onClick={() => {
                router.push(`/property/${el.id}`)
              }}
              icon={<Key />}>
              <Grid xs={2 / 3}>
                <Title>{el.title}</Title>
                <Category>{el.category}</Category>
                <Description>{el.description}</Description>
              </Grid>
              <Grid xs={1 / 3}>
                <Info>
                  <span>{t('Value')}: </span>
                  {numberWithCommas(el.price)}€
                </Info>
                <Info>
                  <span>{t('Yield')}: </span>
                  {el.yield}%
                </Info>
                <Info>
                  <span>{t('Keys')}: </span>
                  {el.noRooms}
                </Info>
              </Grid>
            </CardWithImage>
          </CardContainer>
        )
      })

    return (
      <section>
        <CustomThemeContainer>
          {properties.length === 0 ? (
            <NoProperty>{t('You have no favorite properties')}</NoProperty>
          ) : (
            <Container>
              <SortContainer sm={1 / 2}>
                <DropdownTitle className='sort-title'>
                  {t('Sort By')}
                </DropdownTitle>
                <Dropdown
                  placeholder={t('Select sort option')}
                  options={sortOptions}
                  handleSelect={this.handleSort}
                  selectedOption={selectedSort}
                  accessor={d => d.label}
                />
              </SortContainer>
              <div>{list}</div>
            </Container>
          )}
        </CustomThemeContainer>
      </section>
    )
  }
}

FavoriteProperties.propTypes = {
  user: PropTypes.object,
  t: PropTypes.func,
  router: PropTypes.object
}

export default translate([ 'common', 'favorite' ], { wait: true })(
  FavoriteProperties
)



// WEBPACK FOOTER //
// ./src/pages/Favorite/FavoriteProperties.js